<template>
    <div class="request-chat">
        <VScroll
            ref="scrollbar"
            suppress-scroll-x
            @scroll-up="chatScrollUp"
            @reach-end="chatScrollReachEnd"
            @scroll-down="chatScrollDown"
        >
            <ul class="request-chat__comments">
                <template v-for="comment in comments">
                    <li
                        class="request-chat__comment"
                        :class="{
                            'request-chat__comment_right': isUserAuthorOfComment(comment),
                            'request-chat__comment_unread': !isCommentRead(comment),
                            'request-chat__comment_highlight': highlightedCommentId === comment.id,
                        }"
                        :key="comment.id"
                        :ref="`chatComment_${comment.id}`"
                        @mouseenter="activeCommentId = comment.id"
                        @mouseleave="closeContextMenu"
                        @click="activeCommentId = comment.id"
                    >
                        <ChatComment
                            :request="request"
                            :comment="comment"
                            :reply-source="replySourceOf(comment)"
                            @quoteClick="scrollToComment"
                        ></ChatComment>
                        <div class="request-chat__comment_edit">
                            <button
                                type="button"
                                class="request-chat__comment_edit-button"
                                aria-label="Действия с сообщением"
                                v-show="hasCommentActions(comment) && activeCommentId === comment.id"
                                @click.stop="showContextMenu(comment.id)"
                            >
                                <DotsMenuSvg :size="20"></DotsMenuSvg>
                            </button>
                        </div>
                        <div
                            class="request-chat__comment_edit-menu"
                            v-show="isMenuVisible && activeCommentId === comment.id"
                        >
                            <button
                                type="button"
                                class="request-chat__comment_edit-item request-chat__comment_edit-item-reply"
                                v-if="isAllowToReply"
                                @click="startReply(comment)"
                            >
                                <ReplySvg :size="18"></ReplySvg>
                                <span>Ответить</span>
                            </button>
                            <button
                                type="button"
                                class="request-chat__comment_edit-item request-chat__comment_edit-item-edit"
                                v-if="isAllowToEdit(comment)"
                                @click="editComment(comment)"
                            >
                                <span>Редактировать комментарий</span>
                            </button>
                        </div>
                    </li>
                </template>
            </ul>
        </VScroll>

        <div class="chat-control">
            <div class="chat-control__edit" v-if="showEditButton">
                <EditIcon></EditIcon>
                <span class="chat-control__edit-comment">{{ commentToEdit }}</span>
                <button type="button" class="chat-control__edit-close" @click="closeEdit" v-tooltip="'Выйти'">
                    <CloseEditIcon></CloseEditIcon>
                </button>
            </div>
            <div class="chat-control__reply" v-if="replyTo">
                <ReplySvg class="chat-control__reply-icon" :size="20"></ReplySvg>
                <ChatReplyQuote class="chat-control__reply-quote" :preview="replyToPreview"></ChatReplyQuote>
                <button
                    type="button"
                    class="chat-control__edit-close chat-control__reply-close"
                    aria-label="Отменить ответ"
                    v-tooltip="'Отменить ответ'"
                    @click="cancelReply"
                >
                    <CloseEditIcon></CloseEditIcon>
                </button>
            </div>
            <div
                class="chat-control__row chat-control__row_textarea"
                :class="{
                    'chat-control__row_textarea-stripped': attachments.length > 0,
                }"
            >
                <div class="chat-control__actions">
                    <button
                        type="button"
                        class="chat-control__action"
                        v-tooltip="'Отправить задачу в Bitrix24'"
                        @click.stop="$emit('sendToBitrix24')"
                    >
                        <Bitrix24Svg></Bitrix24Svg>
                    </button>

                    <button class="chat-control__action" @click="showFileUploader" v-tooltip="'Прикрепить файл'">
                        <PaperclipSvg></PaperclipSvg>

                        <FileUploader
                            ref="uploader"
                            v-model="attachments"
                            v-show="false"
                            :endpoint="`/requests/${request.id}/files`"
                            :max-file-size="104857600"
                            max-file-size-inclusive
                            file-size-error-message="Максимальный размер файла — 100 МБ"
                        ></FileUploader>
                    </button>
                </div>

                <div class="chat-control__textarea">
                    <VContenteditable
                        class="chat-control__contenteditable"
                        :showSubmitButton="showSubmitButton"
                        :allow-submit="isAllowSubmit"
                        :send-hotkey-mode="sendHotkeyMode"
                        @submit="submit"
                        v-model="comment"
                        :request="request"
                        :showEditButton="showEditButton"
                        @edit="edit"
                        @keydown.escape.exact.prevent="handleEscKeyDown"
                        @keydown.up.exact="editLast"
                        @input="saveComment"
                        ref="input"
                    ></VContenteditable>
                </div>
            </div>

            <VScroll
                class="chat-control__row chat-control__row_attachments"
                suppress-scroll-y
                v-if="attachments.length > 0"
            >
                <ul class="chat-control__attachments">
                    <template v-for="(attachment, index) in attachments">
                        <li class="chat-control__attachment" :key="index">
                            <ChatCommentAttachment
                                allow-deletion
                                :attachment="attachment"
                                @delete="deleteAttachment"
                                @retry="retryAttachment"
                            ></ChatCommentAttachment>
                        </li>
                    </template>
                </ul>
            </VScroll>
        </div>
        <VDropzone :message="'Прикрепить файлы'" @dropFiles="handleDrop"></VDropzone>
    </div>
</template>
<script>
// Components
import VScroll from '@/views/components/VScroll/VScroll.vue';
import UserCard from '@/views/components/VUser/UserCard.vue';
import FileUploader from '@/views/components/FileUploader/FileUploader.vue';
import VContenteditable from '@/views/components/VContenteditable/VContenteditable.vue';
import VDropzone from '../../../components/VDropzone/VDropzone.vue';

import RequestStatusType from '@/js/enums/RequestStatusType';

import ChatComment from './ChatComment.vue';
import ChatCommentAttachment from './ChatCommentAttachment.vue';
import ChatReplyQuote from './ChatReplyQuote.vue';
import RequestCommentType from '@/js/enums/RequestCommentType';
import { hasReply, indexCommentsById, replyPreview } from '@/js/chatReply';

import DotsMenuSvg from 'vue-material-design-icons/DotsHorizontal.vue';
import HistorySvg from '@/images/history.svg';
import Bitrix24Svg from '@/images/bitrix24.svg';
import PaperclipSvg from '@/images/paperclip.svg';
import UserGroupSvg from '@/images/user-group.svg';
import EditIcon from 'vue-material-design-icons/Pencil';
import CloseEditIcon from 'vue-material-design-icons/Close';
import ReplySvg from 'vue-material-design-icons/Reply.vue';
import last from 'lodash/last';

// Сколько держится подсветка сообщения после перехода к нему по цитате.
const HIGHLIGHT_DURATION = 1600;

// Other
import Vue from 'vue';
import Component from 'vue-class-component';
import { Prop, Ref, Watch } from 'vue-property-decorator';
import ModalNames from '../../../../js/enums/ModalNames';
import RequestHistoryActionType from '../../../../js/enums/RequestHistoryActionType';
import http from '@/js/http';

@Component({
    components: {
        UserCard,
        FileUploader,
        VContenteditable,
        VScroll,
        VDropzone,

        ChatComment,
        ChatCommentAttachment,
        ChatReplyQuote,

        ReplySvg,
        HistorySvg,
        Bitrix24Svg,
        PaperclipSvg,
        UserGroupSvg,
        DotsMenuSvg,
        EditIcon,
        CloseEditIcon,
    },
})
class RequestViewTabChat extends Vue {
    @Ref('uploader')
    uploader;

    @Prop({ type: Object, required: true })
    request;

    isScrollInEnd = true;
    isMenuVisible = false;
    showEditButton = false;
    showSubmitButton = true;

    comment = null;
    commentToEdit = null;
    activeCommentId = null;
    editableCommentId = null;
    attachments = [];
    // Сообщение, на которое отвечаем; null — обычное сообщение.
    replyTo = null;
    highlightedCommentId = null;
    highlightTimer = null;
    // 'enter' — то же поведение, что было всегда, до получения ответа от /account-settings и на
    // случай, если пользователь настройку не трогал. Пользователь может нажать Enter быстрее, чем
    // придёт ответ — в этот момент режим обязан быть именно этим значением, а не undefined.
    sendHotkeyMode = 'enter';

    submit() {
        let comment = this.comment;

        if (this.request.status === RequestStatusType.CLOSED) {
            const link =
                window.location.origin +
                this.$router.resolve({
                    name: 'request',
                    params: {
                        method: 'view',
                        id: this.request.id,
                    },
                }).href;

            comment = `Ссылка на предыдущее обращение: ${link}\n\n` + comment;

            const serviceId = this.request.serviceId;
            const categoryId = this.request.categoryId;

            this.$showModal(ModalNames.CONFIRM, {
                text: 'Данное обращение уже закрыто. Для получения дополнительной информации или по новым вопросам, пожалуйста, создайте новое обращение.',
                title: 'Обращение закрыто',
                cancelTitle: 'Отмена',
                confirmTitle: 'Создать новое обращение',
                confirmAction: () => {
                    this.comment = null;
                    localStorage.removeItem('comment.' + this.request.id);
                    this.attachments = [];

                    this.$router.push({
                        query: {
                            comment,
                            serviceId: serviceId,
                            categoryId: categoryId,
                        },
                    });
                    this.$showModal(ModalNames.REQUEST_CREATE);
                },
            });
            return;
        }

        try {
            if (this.request.status === RequestStatusType.DENIED && this.request.authorId === this.$_account.id) {
                this.$showModal(ModalNames.CONFIRM, {
                    text: 'Вам отказано в обращении. Выберите действие: принять или вернуть в работу!',
                    title: 'Вам отказано в обращении',
                    cancelTitle: 'Вернуть в работу',
                    confirmTitle: 'Принять работу',
                    cancelAction: () => this.$emit('changeStatus', this.request, RequestStatusType.WORKUP),
                    confirmAction: () => this.$emit('changeStatus', this.request, RequestStatusType.CLOSED),
                });
            }

            const files = this.attachments.filter((attachment) => attachment.id > 0).map((attachment) => attachment.id);

            if (this.isAllowSubmit) {
                const replyToId = this.replyTo?.id ?? null;
                this.$emit('addComment', replyToId ? { files, comment, replyToId } : { files, comment });
            }
        } finally {
            this.comment = null;
            localStorage.removeItem('comment.' + this.request.id);
            this.attachments = [];
            this.replyTo = null;
        }
    }

    startReply(comment) {
        if (this.showEditButton) {
            this.closeEdit();
        }

        this.replyTo = comment;
        this.$refs.input?.focus?.();
    }

    cancelReply() {
        this.replyTo = null;
    }

    replySourceOf(comment) {
        return hasReply(comment) ? this.commentsById[comment.replyToId] || null : null;
    }

    scrollToComment(commentId) {
        const target = this.$refs[`chatComment_${commentId}`]?.[0];
        const container = this.$refs.scrollbar?.$el;

        if (!target || !container) {
            return;
        }

        // Ставим исходное сообщение в середину видимой области. Геометрию берём из
        // getBoundingClientRect: offsetTop зависит от того, какой предок спозиционирован.
        const delta =
            target.getBoundingClientRect().top -
            container.getBoundingClientRect().top -
            (container.clientHeight - target.offsetHeight) / 2;
        const top = Math.max(0, container.scrollTop + delta);

        if (typeof container.scrollTo === 'function') {
            container.scrollTo({ top, behavior: 'smooth' });
        } else {
            container.scrollTop = top;
        }

        this.highlightComment(commentId);
    }

    highlightComment(commentId) {
        clearTimeout(this.highlightTimer);
        // Повторный клик по той же цитате должен заново запустить анимацию.
        this.highlightedCommentId = null;
        this.$nextTick(() => {
            this.highlightedCommentId = commentId;
            this.highlightTimer = setTimeout(() => {
                this.highlightedCommentId = null;
            }, HIGHLIGHT_DURATION);
        });
    }

    deleteAttachment(attachment) {
        const index = this.attachments.indexOf(attachment);

        if (index !== -1) {
            this.attachments.splice(index, 1);
        }
    }

    retryAttachment(attachment) {
        this.uploader.retry(attachment);
    }

    showFileUploader() {
        if (this.uploader?.showFileUploader) {
            this.uploader.showFileUploader();
        }
    }

    chatScrollUp() {
        this.isScrollInEnd = false;
    }

    chatScrollReachEnd() {
        this.isScrollInEnd = true;
    }

    chatScrollDown() {
        const scrollTop = this.$refs.scrollbar.$el.scrollTop;
        const scrollHeight = this.$refs.scrollbar.$el.clientHeight;
        for (let i = this.comments.length - 1; i >= 0; i--) {
            if (this.isCommentRead(this.comments[i])) {
                break;
            }
            const messageComponent = this.$refs[`chatComment_${this.comments[i].id}`][0];
            if (messageComponent.offsetTop <= scrollTop + scrollHeight) {
                this.$emit('readComment', this.comments[i]);
            }
        }
    }

    scrollToEnd() {
        const element = this.$refs.scrollbar?.$el;

        if (element) {
            element.scrollTop = element.scrollHeight;
        }
    }

    isUserAuthorOfComment(comment) {
        return comment.authorId == this.$_account.id;
    }

    isCommentRead(comment) {
        const lastSeenCommentId = last(this.request.lastSeenComments)?.commentId || 0;
        if (comment.type === RequestCommentType.FIRST_COMMENT && comment.authorId === this.$_account.id) {
            return true;
        } else {
            return comment.id <= lastSeenCommentId;
        }
    }

    isAllowToEdit(comment) {
        return (
            this.isUserAuthorOfComment(comment) &&
            comment.type === RequestCommentType.COMMENT &&
            !this.comment &&
            this.request.status !== RequestStatusType.CLOSED
        );
    }

    get comments() {
        return this.request.comments ?? [];
    }

    get commentsById() {
        return indexCommentsById(this.comments);
    }

    get replyToPreview() {
        return replyPreview(this.replyTo);
    }

    get isAllowToReply() {
        // В закрытом обращении сообщение не отправить — отправка ведёт к созданию нового.
        return this.request.status !== RequestStatusType.CLOSED;
    }

    // Меню «⋯» нужно, если доступно хоть одно действие. Права каждого действия
    // проверяются отдельно: ответ не расширяет права на редактирование.
    hasCommentActions(comment) {
        return this.isAllowToReply || this.isAllowToEdit(comment);
    }

    get isAllowSubmit() {
        const hasNotUploadedAttachments = this.attachments
            .filter((attachment) => !attachment.errorMessage)
            .every((attachment) => attachment.id > 0);

        const hasAnySuccessfulUploadedAttachment =
            this.attachments.filter((attachment) => attachment.id > 0).length > 0;

        return (this.comment || hasAnySuccessfulUploadedAttachment) && hasNotUploadedAttachments;
    }

    get isAuthorRead() {
        const lastWatchedStoryIndex = this.request.history.findLastIndex(
            (story) => story.action === RequestHistoryActionType.WATCHED && story.authorId === this.request.authorId,
        );
        const hasNewUnreadComments = this.request.history.some(
            (story, index) =>
                index >= lastWatchedStoryIndex &&
                story.action === RequestHistoryActionType.COMMENTED &&
                story.authorId !== this.request.authorId,
        );

        return !hasNewUnreadComments;
    }

    get isExecutorRead() {
        const lastWatchedStoryIndex = this.request.history.findLastIndex(
            (story) => story.action === RequestHistoryActionType.WATCHED && story.authorId === this.request.executorId,
        );
        const hasNewUnreadComments = this.request.history.some(
            (story, index) =>
                index >= lastWatchedStoryIndex &&
                story.action === RequestHistoryActionType.COMMENTED &&
                story.authorId !== this.request.executorId,
        );
        return !hasNewUnreadComments;
    }

    @Watch('request.id')
    requestIdWatcher(newValue, oldValue) {
        oldValue = parseInt(oldValue, 10);
        newValue = parseInt(newValue, 10);

        if (oldValue !== newValue) {
            this.comment = localStorage.getItem('comment.' + this.request.id) ?? null;
            this.activeCommentId = null;
            this.showEditButton = false;
            this.showSubmitButton = true;
            this.attachments = [];
            this.replyTo = null;
            this.highlightedCommentId = null;
        }
    }

    @Watch('request.comments')
    async requestUpdateWatcher() {
        if (this.isScrollInEnd) {
            await this.$nextTick();
            this.scrollToEnd();
        }
    }

    showContextMenu(commentId) {
        this.isMenuVisible = true;
        this.activeCommentId = commentId;
        document.addEventListener('click', this.closeContextMenu);
    }

    closeContextMenu() {
        this.isMenuVisible = false;
        document.removeEventListener('click', this.closeContextMenu);
    }

    closeEdit() {
        this.comment = null;
        this.activeCommentId = null;
        this.showEditButton = false;
        this.showSubmitButton = true;
    }

    editComment(editableComment) {
        this.replyTo = null;
        this.commentToEdit = editableComment.text;
        this.comment = editableComment.text;
        this.editableCommentId = editableComment.id;
        this.showEditButton = true;
        this.showSubmitButton = false;
        this.$refs.input.focus();
    }

    edit(value) {
        const newText = value;
        const commentId = this.editableCommentId;
        this.$emit('editComment', { commentId, newText });
        this.comment = null;
        this.editableCommentId = null;
        this.showEditButton = false;
        this.showSubmitButton = true;
    }

    editLast(event) {
        const lastComment = last(this.comments);
        if (this.isAllowToEdit(lastComment)) {
            event.preventDefault();
            this.editComment(lastComment);
        }
    }

    handleEscKeyDown() {
        if (this.showEditButton) {
            this.closeEdit();
        } else if (this.replyTo) {
            this.cancelReply();
        }
    }

    handleDrop(data) {
        this.uploader.submitFiles(data);
    }

    saveComment(value) {
        if (!this.showEditButton) localStorage.setItem('comment.' + this.request.id, value);
    }

    mounted() {
        this.requestUpdateWatcher();
        this.comment = localStorage.getItem('comment.' + this.request.id) ?? null;
        this.loadSendHotkeyMode();
    }

    beforeDestroy() {
        clearTimeout(this.highlightTimer);
        document.removeEventListener('click', this.closeContextMenu);
    }

    async loadSendHotkeyMode() {
        try {
            const { data } = await http.get('/account-settings');
            this.sendHotkeyMode = data['request-comment.send-hotkey'] || 'enter';
        } catch (error) {
            //
        }
    }
}

export default RequestViewTabChat;
</script>
<style lang="scss">
:root {
    --chat-control-shadow: rgba(88, 88, 88, 0.15);
    --chat-control-action-fill: #aaaaaa;
    --chat-control-action-fill-hover: #484848;
    --chat-control-action-fill-active: #00a55a;
}

.request-chat {
    display: flex;
    width: 100%;
    height: 100%;
    flex-direction: column;

    &__comments {
        margin: 24px 0;
        min-height: none;
    }

    &__comment {
        display: flex;
        max-height: 100%;
        padding: 12px 0 12px 0;
        justify-content: flex-start;
        position: relative;

        &_right {
            justify-content: flex-end;
        }

        &_unread {
            background: #f0f0f0;
        }

        &_highlight {
            animation: request-chat-comment-highlight 1.6s ease-out;
        }

        &_edit {
            position: absolute;
            top: 0;
            right: 0;
            &-button {
                position: relative;
                display: flex;
                align-items: center;
                justify-content: center;
                width: 30px;
                height: 30px;
                margin: 0 15px;
                padding: 8px;
                fill: var(--chat-control-action-fill);
                background-color: var(--color-white);
                border: 1px solid var(--chat-control-action-fill);
                border-radius: 8px;
                cursor: pointer;
                &:hover,
                &:focus-visible {
                    fill: var(--chat-control-action-fill-hover);
                    border: 1px solid var(--chat-control-action-fill-hover);
                }
            }

            &-menu {
                position: absolute;
                top: 0;
                right: 45px;
                display: flex;
                flex-direction: column;
                width: max-content;
                max-width: calc(100% - 60px);
                border-radius: 10px;
                background-color: var(--color-white);
                box-shadow: 0px 2px 15px 3px rgba(44, 44, 44, 0.12);
                color: var(--text-black);
                z-index: 1;
            }

            &-item {
                display: flex;
                align-items: center;
                gap: 8px;
                width: 100%;
                min-height: 40px;
                padding: 0 16px;
                border: 0;
                background: transparent;
                color: inherit;
                font: inherit;
                text-align: left;
                cursor: pointer;

                &:hover,
                &:focus-visible {
                    background: #f8f8f8;
                }

                & .material-design-icon {
                    display: inline-flex;
                    fill: var(--chat-control-action-fill-hover);
                }
            }
        }
    }
}

// У чужих сообщений «⋯» стоит слева — меню раскрывается вправо, чтобы не уйти за край.
.request-chat__comment:not(.request-chat__comment_right) .request-chat__comment_edit-menu {
    right: auto;
    left: 45px;
}

.request-chat__comment:not(.request-chat__comment_right) .request-chat__comment_edit {
    right: auto;
    left: 0;
}

.chat-control {
    padding: 24px;
    max-height: 100%;
    box-shadow: 0px 1px 10px var(--chat-control-shadow);
    box-sizing: control-box;
    width: 100%;

    &__row {
        display: flex;
        align-items: flex-end;

        &_textarea {
            min-height: 72px;
            max-height: 100%;
        }

        &_textarea-stripped {
            max-height: calc(100% - 72px);
        }

        &_attachments {
            height: auto !important;
            min-height: 72px;
        }
    }

    &__action {
        width: 38px;
        height: 38px;
        margin: 5px;
        padding: 8px;
        fill: var(--chat-control-action-fill);
        border: 1px solid var(--chat-control-action-fill);
        border-radius: 8px;
        cursor: pointer;

        &:hover,
        &:focus-visible {
            fill: var(--chat-control-action-fill-hover);
            border: 1px solid var(--chat-control-action-fill-hover);
        }

        &:active {
            fill: var(--chat-control-action-fill-active);
            border: 1px solid var(--chat-control-action-fill-active);
        }
    }

    &__actions {
        flex: 0 0 96px;
        width: 96px;
        height: 48px;
        margin: -5px;
    }

    &__textarea {
        width: 100%;
        min-height: 72px;
        max-height: 100%;
        margin-left: 16px;
    }

    &__attachment {
        margin: 0 8px 0 0;
    }

    &__attachments {
        display: flex;
        margin: 8px 0 0 0;
    }

    &__edit {
        line-height: 40px;
        border-bottom: 1px solid #e4eaef;
        margin-bottom: 20px;
        padding-bottom: 5px;
        display: flex;
        align-items: center;

        &-comment {
            margin-left: 10px;
            width: 100%;
            overflow: hidden;
            text-overflow: ellipsis;
        }

        &-close {
            margin-left: auto;
            width: 30px;
            height: 30px;
            outline: none;
            border: 0;
            border-radius: 50%;
            cursor: pointer;
            z-index: 2;
            transition: 0.06s;
        }
    }

    &__reply {
        display: flex;
        align-items: center;
        gap: 8px;
        min-width: 0;
        margin-bottom: 12px;
        padding-bottom: 8px;
        border-bottom: 1px solid #e4eaef;

        &-icon {
            display: inline-flex;
            flex: 0 0 auto;
            fill: var(--color-green);
        }

        &-quote {
            flex: 1 1 auto;
        }

        &-close {
            flex: 0 0 auto;
        }
    }
}

@keyframes request-chat-comment-highlight {
    0%,
    40% {
        background-color: rgba(0, 165, 90, 0.16);
    }
    100% {
        background-color: transparent;
    }
}

@media (prefers-reduced-motion: reduce) {
    .request-chat__comment_highlight {
        animation: none;
        background-color: rgba(0, 165, 90, 0.16);
    }
}
@media (max-width: $mobile-breakpoint) {
    .chat-control {
        padding: 20px;

        &__row_textarea {
            min-height: 50px;
        }

        &__textarea {
            min-height: 50px;
            .contenteditable__input {
                min-height: 50px;
            }
        }
    }
}
</style>
